import * as React from "react";
import {
  AuthShell,
  AuthCard,
  AuthFieldRow,
  AuthErrorAlert,
  AuthBadge,
  PasswordInput,
  PasswordRequirements,
  Button,
  passwordChecks,
} from "@aisquare/ui";
import { Mail, Lock } from "lucide-react";
import { Section, VariantBlock } from "../lib/Section";

/**
 * AuthShell is `fixed inset-0 z-[300]`, so a raw mount would escape its
 * container and cover the whole playground. Every block that renders the shell
 * wraps it in a `relative` framed window (the same trick the stories use) so
 * the full-page stage is captured in-place. `background="none"` keeps the
 * canvas flat here since the playground ships no `/logo-mark.png` for the
 * default logo-mask backdrop.
 */
const FRAME = "relative h-[460px] w-full max-w-[520px] overflow-hidden rounded-xl border border-border";

export function AuthShellDemo() {
  return (
    <Section
      id="auth-shell"
      title="AuthShell"
      description="The full-page stage the auth route mounts (login / signup / forgot / reset), distinct from the dismissible AuthModal. Pairs with AuthCard plus the auth-slot atoms (AuthFieldRow, AuthErrorAlert, AuthBadge, PasswordRequirements), replacing the scaffold the three unified auth screens each copy-pasted by hand."
    >
      <VariantBlock label="Sign-in route" caption="AuthShell + AuthCard composing the email-first door">
        <div className={FRAME}>
          <AuthShell background="none" className="!absolute">
            <AuthCard logoSrc={null}>
              <h2 className="mb-4 text-[27px] font-normal leading-[1.15] tracking-[-0.01em] text-foreground">
                Sign in to your account
              </h2>
              <form onSubmit={(e) => e.preventDefault()}>
                <AuthFieldRow
                  label="Email"
                  htmlFor="as-email"
                  icon={<Mail className="h-[15px] w-[15px]" />}
                  inputProps={{
                    id: "as-email",
                    type: "email",
                    placeholder: "you@example.com",
                    autoComplete: "username",
                  }}
                />
                <Button type="submit" className="mt-3 w-full">
                  Continue
                </Button>
              </form>
              <p className="mt-[18px] text-center text-[12.5px] text-muted-foreground">
                New here? <b className="font-semibold text-foreground">Create account</b>
              </p>
            </AuthCard>
          </AuthShell>
        </div>
      </VariantBlock>

      <VariantBlock label="Auth-slot atoms" caption="AuthBadge tones and the inline AuthErrorAlert, standalone">
        <div className="flex flex-wrap items-start gap-6">
          <div className="flex flex-col items-center gap-2">
            <AuthBadge tone="accent" icon={<Mail className="h-[22px] w-[22px]" />} />
            <span className="text-[11px] text-muted-foreground">tone=&quot;accent&quot;</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <AuthBadge tone="success" icon={<Lock className="h-[22px] w-[22px]" />} />
            <span className="text-[11px] text-muted-foreground">tone=&quot;success&quot;</span>
          </div>
          <div className="w-64">
            <AuthErrorAlert animate={false}>Incorrect email or password.</AuthErrorAlert>
          </div>
        </div>
      </VariantBlock>

      <VariantBlock label="AuthFieldRow" caption="labeled field with a leading icon, the brand focus ring, and an inline error">
        <div className="grid w-72 gap-4">
          <AuthFieldRow
            label="Email"
            htmlFor="as-row-email"
            icon={<Mail className="h-[15px] w-[15px]" />}
            inputProps={{ id: "as-row-email", type: "email", placeholder: "you@example.com" }}
          />
          <AuthFieldRow
            label="Email"
            htmlFor="as-row-error"
            icon={<Mail className="h-[15px] w-[15px]" />}
            error="That email is not registered."
            inputProps={{ id: "as-row-error", type: "email", defaultValue: "ghost@nowhere.io" }}
          />
        </div>
      </VariantBlock>

      <VariantBlock label="Reset form (live)" caption="PasswordInput + PasswordRequirements share one rule set; the checklist flips and the CTA gates as you type">
        <div className={FRAME}>
          <ResetForm />
        </div>
      </VariantBlock>
    </Section>
  );
}

/**
 * The reset-password route, wired to real state so PasswordRequirements
 * actually flips per rule and the submit button gates until every check (and
 * the confirm match) passes. The headline reuse proof: PasswordInput +
 * PasswordRequirements driven by the shared `passwordChecks` rule.
 */
function ResetForm() {
  const [pw, setPw] = React.useState("");
  const [pw2, setPw2] = React.useState("");
  const checks = passwordChecks(pw);
  const allMet = Object.values(checks).every(Boolean);
  const match = pw.length > 0 && pw === pw2;

  return (
    <AuthShell background="none" className="!absolute">
      <AuthCard logoSrc={null}>
        <AuthBadge tone="success" icon={<Lock className="h-[22px] w-[22px]" />} />
        <h2 className="mb-4 mt-4 text-[27px] font-normal leading-[1.15] tracking-[-0.01em] text-foreground">
          Reset your password
        </h2>
        <form onSubmit={(e) => e.preventDefault()} className="grid gap-3">
          <AuthFieldRow label="New password" htmlFor="as-rp-pw">
            <PasswordInput
              id="as-rp-pw"
              autoComplete="new-password"
              placeholder="Create a password"
              value={pw}
              onValueChange={setPw}
              valid={allMet}
            />
          </AuthFieldRow>
          <PasswordRequirements value={pw} />
          <AuthFieldRow label="Confirm password" htmlFor="as-rp-pw2">
            <PasswordInput
              id="as-rp-pw2"
              autoComplete="new-password"
              placeholder="Type it again"
              value={pw2}
              onValueChange={setPw2}
              valid={match}
            />
          </AuthFieldRow>
          <Button type="submit" className="w-full" disabled={!allMet || !match}>
            Reset password
          </Button>
        </form>
      </AuthCard>
    </AuthShell>
  );
}
